import { scopeId, splitScopedId } from './filterBarIds';
import type { DashboardSourceColumn, ScopedColumn } from './types';

/**
 * Identity of a column inside the widget panel, once it mixes tables (SL-1792).
 *
 * The panel offers the main table's columns and those of every ready connection
 * in the same pickers, and column ids are only unique within their own table
 * (see `filterBarIds.ts`). Inside the panel a column is therefore named by a
 * **key**:
 *
 * - a column of the main table → its plain `columnId`;
 * - a column of a connected table → `scopeId(tableId, columnId)`.
 *
 * The main table keeps the plain id on purpose: every widget saved before
 * connected tables existed stores bare column ids, and the issues, the Select
 * values and the draft operations all keep reading them exactly as before.
 *
 * A key never leaves the front end. The draft stores `{ columnId, tableId? }`
 * (ADR-047 point 5), and this module is the only place that goes from one to the
 * other.
 */

/** Something stored against a column: a field, or a filter. */
export interface ColumnRef {
  columnId: string;
  /** Absent for the main table. */
  tableId?: string;
}

/**
 * What a field or filter stores for a column: its table only when it is a
 * connected one. The one place that writes that rule — absent means the main
 * table (ADR-047 point 5), so a main column never carries `tableId: undefined`.
 */
export const refOf = (columnId: string, tableId?: string): ColumnRef =>
  tableId ? { columnId, tableId } : { columnId };

/** The key of a stored field or filter. */
export const fieldKeyOf = ({ columnId, tableId }: ColumnRef): string =>
  tableId ? scopeId(tableId, columnId) : columnId;

/** Whether a column says its table. One that does not is the main table's. */
export const isScoped = (column: DashboardSourceColumn | ScopedColumn): column is ScopedColumn =>
  'tableId' in column;

/** The key of a column of the schema. */
export const columnKeyOf = (column: DashboardSourceColumn | ScopedColumn): string =>
  isScoped(column) && !column.isMain ? scopeId(column.tableId, column.id) : column.id;

/** What a key stores in the draft: the column, and its table only when it is not the main one. */
export function refOfKey(key: string): ColumnRef {
  const { tableId, columnId } = splitScopedId(key);
  return refOf(columnId, tableId ?? undefined);
}

/**
 * Names shared by columns of different tables, among the ones given.
 *
 * Only those get their table beside the name once chosen ("Monto · Clientes"):
 * with one table, or with names that do not repeat, the table is noise taking
 * room from the name (decided at G1).
 */
export function repeatedNames(columns: ScopedColumn[]): ReadonlySet<string> {
  const tablesByName = new Map<string, Set<string>>();
  columns.forEach((column) => {
    const tables = tablesByName.get(column.name) ?? new Set<string>();
    tables.add(column.tableId);
    tablesByName.set(column.name, tables);
  });
  return new Set(
    Array.from(tablesByName)
      .filter(([, tables]) => tables.size > 1)
      .map(([name]) => name),
  );
}

/**
 * Separator between a column and its table when the name repeats.
 *
 * Punctuation, not words, so it needs no translation.
 */
const QUALIFIER = ' · ';

/** A column's name, with its table beside it when that name repeats. */
export const qualifiedName = (column: ScopedColumn, repeated: ReadonlySet<string>): string =>
  repeated.has(column.name) ? `${column.name}${QUALIFIER}${column.tableName}` : column.name;
